import * as pc from 'playcanvas';

interface Particle {
  entity: pc.Entity;
  velocity: pc.Vec3;
  life: number;
  maxLife: number;
  color: pc.Color;
}

export class ParticleSystem {
  private app: pc.Application;
  private particles: Particle[] = [];
  private pool: pc.Entity[] = [];

  constructor(app: pc.Application) {
    this.app = app;
  }

  private getParticleEntity(): pc.Entity {
    if (this.pool.length > 0) return this.pool.pop()!;
    const e = new pc.Entity('particle');
    // Each pooled entity owns one material that is reused across its lifetime cycles.
    // emissive/opacity are updated at spawn time rather than creating a new material.
    const mat = new pc.StandardMaterial();
    mat.emissiveIntensity = 2;
    mat.blendType = pc.BLEND_ADDITIVE;
    mat.depthWrite = false;
    mat.update();
    e.addComponent('render', { type: 'sphere', material: mat });
    e.render!.castShadows = false;
    e.render!.receiveShadows = false;
    this.app.root.addChild(e);
    return e;
  }

  explosion(x: number, y: number, z: number, scale = 1) {
    const count = Math.floor(12 * scale);
    const colors = [
      new pc.Color(1, 0.5, 0.1),
      new pc.Color(1, 0.9, 0.2),
      new pc.Color(1, 1, 1),
    ];

    for (let i = 0; i < count; i++) {
      const e = this.getParticleEntity();
      const size = (0.1 + Math.random() * 0.25) * scale;
      e.setLocalScale(size, size, size);

      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      const spd = 4 + Math.random() * 8 * scale;

      const vel = new pc.Vec3(
        Math.sin(phi) * Math.cos(theta) * spd,
        Math.abs(Math.cos(phi)) * spd * 0.8,
        Math.sin(phi) * Math.sin(theta) * spd,
      );

      e.setPosition(x, y + 0.5, z);
      e.enabled = true;

      const color = colors[Math.floor(Math.random() * colors.length)].clone();
      const mat = e.render!.meshInstances[0].material as pc.StandardMaterial;
      mat.emissive = color;
      mat.opacity = 1;
      mat.update();

      this.particles.push({
        entity: e,
        velocity: vel,
        life: 0,
        maxLife: 0.4 + Math.random() * 0.4,
        color,
      });
    }
  }

  muzzleFlash(x: number, y: number, z: number) {
    for (let i = 0; i < 4; i++) {
      const e = this.getParticleEntity();
      const size = 0.08 + Math.random() * 0.12;
      e.setLocalScale(size, size, size);
      e.setPosition(x, y, z);
      e.enabled = true;

      const vel = new pc.Vec3(
        (Math.random() - 0.5) * 3,
        Math.random() * 3,
        (Math.random() - 0.5) * 3,
      );

      const muzzleColor = new pc.Color(1, 0.9, 0.3);
      const mat = e.render!.meshInstances[0].material as pc.StandardMaterial;
      mat.emissive = muzzleColor;
      mat.opacity = 1;
      mat.update();

      this.particles.push({
        entity: e,
        velocity: vel,
        life: 0,
        maxLife: 0.15,
        color: muzzleColor,
      });
    }
  }

  /**
   * Large destruction burst for a tank kill.
   * Fires two overlapping fireballs and a rising smoke plume.
   * isLocal = true makes it ~50% bigger for dramatic self-death effect.
   */
  tankDestroyed(x: number, y: number, z: number, isLocal: boolean) {
    const scale = isLocal ? 3.5 : 2.2;
    // Primary fireball
    this.explosion(x, y, z, scale);
    // Secondary burst slightly above — gives the classic double-flash look
    this.explosion(x, y + 0.4, z, scale * 0.55);

    // Rising smoke puffs (dark additive particles read as grey smoke)
    const smokeCount = isLocal ? 10 : 6;
    for (let i = 0; i < smokeCount; i++) {
      const e = this.getParticleEntity();
      const size = (0.2 + Math.random() * 0.35) * scale * 0.25;
      e.setLocalScale(size, size, size);
      e.setPosition(x + (Math.random() - 0.5) * 0.6, y + 0.3, z + (Math.random() - 0.5) * 0.6);
      e.enabled = true;

      const gv = 0.12 + Math.random() * 0.12;
      const smokeColor = new pc.Color(gv, gv * 0.9, gv * 0.8);
      const mat = e.render!.meshInstances[0].material as pc.StandardMaterial;
      mat.emissive = smokeColor;
      mat.opacity = 1;
      mat.update();

      const vel = new pc.Vec3(
        (Math.random() - 0.5) * 2.0,
        1.5 + Math.random() * 2.5,
        (Math.random() - 0.5) * 2.0,
      );
      this.particles.push({ entity: e, velocity: vel, life: 0, maxLife: 0.9 + Math.random() * 0.7, color: smokeColor });
    }
  }

  crystalBurst(x: number, y: number, z: number) {
    for (let i = 0; i < 8; i++) {
      const e = this.getParticleEntity();
      const size = 0.12 + Math.random() * 0.18;
      e.setLocalScale(size, size, size);
      e.setPosition(x, y + 0.3, z);
      e.enabled = true;

      const theta = (i / 8) * Math.PI * 2;
      const vel = new pc.Vec3(
        Math.cos(theta) * 5,
        3 + Math.random() * 4,
        Math.sin(theta) * 5,
      );

      const crystalColor = new pc.Color(0.1, 0.9, 1);
      const mat = e.render!.meshInstances[0].material as pc.StandardMaterial;
      mat.emissive = crystalColor;
      mat.opacity = 1;
      mat.update();

      this.particles.push({
        entity: e,
        velocity: vel,
        life: 0,
        maxLife: 0.7 + Math.random() * 0.3,
        color: crystalColor,
      });
    }
  }

  update(dt: number) {
    const gravity = -9.8;
    const toRemove: number[] = [];

    for (let i = 0; i < this.particles.length; i++) {
      const p = this.particles[i];
      p.life += dt;

      if (p.life >= p.maxLife) {
        p.entity.enabled = false;
        this.pool.push(p.entity);
        toRemove.push(i);
        continue;
      }

      // Physics
      p.velocity.y += gravity * dt;
      const pos = p.entity.getPosition();
      pos.x += p.velocity.x * dt;
      pos.y += p.velocity.y * dt;
      pos.z += p.velocity.z * dt;
      if (pos.y < 0.05) { pos.y = 0.05; p.velocity.y *= -0.3; }
      p.entity.setPosition(pos.x, pos.y, pos.z);

      // Fade
      const t = p.life / p.maxLife;
      const mat = p.entity.render!.meshInstances[0].material as pc.StandardMaterial;
      mat.opacity = 1 - t;
      mat.update();
    }

    // Remove dead particles (reverse to preserve indices)
    for (let i = toRemove.length - 1; i >= 0; i--) {
      this.particles.splice(toRemove[i], 1);
    }
  }
}
